import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Clock, FileType2, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { APOLLO_LOGO } from "@/lib/brand";

/**
 * Public asset-share landing page. No auth required — the token is the credential.
 * Renders a minimal Apollo-branded shell with a preview, expiry countdown, and
 * a single "Download" button that hits /api/share/:token/download.
 */
export default function PublicShare() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [busy, setBusy] = useState(false);
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/share/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) setState({ status: "error", data: null, error: body.detail || (res.status === 410 ? "This link has expired." : "This link is invalid or has been revoked.") });
          return;
        }
        const data = await res.json();
        if (!cancelled) setState({ status: "ready", data, error: null });
      } catch (e) {
        if (!cancelled) setState({ status: "error", data: null, error: "Could not reach the server. Please check your connection." });
      }
    }
    load();
    return () => { cancelled = true; };
  }, [token]);

  // Live countdown to expiry — refreshes every second while the modal is open.
  useEffect(() => {
    if (state.status !== "ready") return;
    const target = new Date(state.data.expires_at).getTime();
    const tick = () => {
      const ms = target - Date.now();
      if (ms <= 0) {
        setTimeLeft("Expired");
        setState((s) => ({ ...s, status: "error", error: "This link has expired." }));
        return;
      }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setTimeLeft(h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [state.status, state.data]);

  async function handleDownload() {
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/share/${token}/download`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail || "Download failed");
        setBusy(false);
        return;
      }
      const disp = res.headers.get("content-disposition") || "";
      const match = disp.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : (state.data?.asset?.title || "download");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Download started");
    } catch (e) {
      toast.error(e?.message || "Download failed");
    }
    setBusy(false);
  }

  const asset = state.data?.asset;
  const preview = asset?.preview_url;
  const resolvedPreview = preview && !preview.startsWith("http")
    ? `${process.env.REACT_APP_BACKEND_URL}${preview}`
    : preview;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="public-share-page">
      {/* Header */}
      <header className="px-6 py-5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={APOLLO_LOGO} alt="Apollo" className="h-6 w-auto" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Brand Portal · Shared File</span>
        </div>
        {state.status === "ready" && (
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground flex items-center gap-1.5" data-testid="share-expiry">
            <Clock className="h-3 w-3" /> Expires in {timeLeft}
          </div>
        )}
      </header>

      {/* Body */}
      <main className="flex-1 flex items-center justify-center px-6 py-10">
        {state.status === "loading" && (
          <div className="text-muted-foreground animate-pulse text-sm font-mono uppercase tracking-widest">Loading…</div>
        )}

        {state.status === "error" && (
          <div className="max-w-md text-center space-y-4" data-testid="share-error">
            <div className="mx-auto w-14 h-14 rounded-full border border-[#E15C2C]/40 bg-[#E15C2C]/10 text-[#E15C2C] flex items-center justify-center">
              <ShieldAlert className="h-6 w-6" />
            </div>
            <div className="font-display text-2xl uppercase font-bold tracking-tight">Link Unavailable</div>
            <p className="text-sm text-muted-foreground">{state.error}</p>
            <p className="text-xs text-muted-foreground">
              If you were expecting this file, please ask the sender to generate a new link.
            </p>
          </div>
        )}

        {state.status === "ready" && asset && (
          <div className="w-full max-w-3xl grid md:grid-cols-5 gap-8">
            <div className="md:col-span-3">
              <div className="aspect-[4/3] rounded-sm border border-border bg-muted overflow-hidden flex items-center justify-center">
                {resolvedPreview ? (
                  <img src={resolvedPreview} alt={asset.title} className="w-full h-full object-contain" data-testid="share-preview-img" />
                ) : (
                  <div className="text-muted-foreground flex flex-col items-center gap-2">
                    <FileType2 className="h-10 w-10" />
                    <span className="font-mono text-[10px] uppercase tracking-widest">No preview</span>
                  </div>
                )}
              </div>
            </div>
            <div className="md:col-span-2 space-y-5">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
                  {asset.category || "Asset"} · {(asset.file_type || "").toUpperCase()}
                </div>
                <h1 className="font-display text-2xl md:text-3xl uppercase font-bold tracking-tight" data-testid="share-asset-title">
                  {asset.title}
                </h1>
                {asset.description && (
                  <p className="text-sm text-muted-foreground mt-3">{asset.description}</p>
                )}
              </div>

              <button
                onClick={handleDownload}
                disabled={busy}
                className="w-full bg-foreground text-background py-3.5 uppercase text-xs font-semibold tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="share-download-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                {busy ? "Preparing…" : "Download"}
              </button>

              {state.data.shared_by && (
                <div className="text-xs text-muted-foreground border-t border-border pt-4">
                  Shared by <span className="text-foreground font-medium">{state.data.shared_by}</span> via Apollo Brand Portal
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      <footer className="px-6 py-4 border-t border-border text-center">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-muted-foreground">
          © Apollo · Brand Portal
        </div>
      </footer>
    </div>
  );
}
